/**
 * Posting a listing, step one: the company, and the money.
 *
 * The order here is deliberate and it is the whole product decision. A company
 * signs in, says who it is once, chooses what it wants to spend, and pays —
 * and only then writes the listing. Nobody fills in a form for a listing that
 * turns out never to be bought, and nobody arrives at a payment screen having
 * already done the work.
 *
 * What the payment buys is a reservation: a listing that exists, is paid for,
 * and is waiting for its details. It publishes the moment those details are
 * submitted, on `/post/finish`, which is where Stripe returns to.
 *
 * The rank preview on this step is against the **global** board, and it says
 * so. The specialism this role competes in is not known yet — it is chosen on
 * the next step — so a figure labelled as a specialism figure would be an
 * invention. This is the honest cost of taking the money first.
 */

import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useState, type ReactNode } from 'react';

import {
  LISTING_LIFETIME_DAYS,
  MINIMUM_FIRST_BID_CENTS,
  type Company,
  type LeaderboardScope,
} from '@bidtohire/shared';

import { AmountStepper } from '../components/AmountStepper';
import { CompanyForm } from '../components/CompanyForm';
import { useCompetingEntries } from '../components/BidPanel';
import { Money } from '../components/Money';
import { Odometer } from '../components/Odometer';
import { rememberPublishing } from '../components/PublishedBeat';
import {
  BoardSkeleton,
  CautionNotice,
  ErrorNotice,
  errorMessage,
} from '../components/Status';
import { ShieldIcon } from '../components/ui';
import { bidPreview, dollarInputValue, parseDollarInput } from '../lib/bid-pricing';
import { useMyCompany, useReserveListing, useSaveCompany } from '../lib/queries';

type Step = 'company' | 'reserve';

const STEPS: readonly { readonly key: Step | 'content'; readonly label: string }[] = [
  { key: 'company', label: 'Company profile' },
  { key: 'reserve', label: 'Reserve' },
  { key: 'content', label: 'Add the posting' },
];

function Steps({ current }: { readonly current: Step }): ReactNode {
  const index = STEPS.findIndex((step) => step.key === current);
  return (
    <ol className="steps">
      {STEPS.map((step, position) => {
        const state =
          step.key === current ? 'is-current' : position < index ? 'is-done' : undefined;
        return (
          <li key={step.key} className={state}>
            <span className="n">
              {state === 'is-done' ? (
                <svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                  <path
                    d="M2.6 6.2l2.3 2.3L9.4 3.9"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : (
                position + 1
              )}
            </span>
            {step.label}
          </li>
        );
      })}
    </ol>
  );
}

export function PostPage(): ReactNode {
  const [params] = useSearchParams();
  const profile = useMyCompany();
  const saveCompany = useSaveCompany();

  const [editingCompany, setEditingCompany] = useState(false);

  if (profile.isLoading) {
    return (
      <div className="inner" style={{ paddingTop: 32 }}>
        <BoardSkeleton rows={3} />
      </div>
    );
  }
  if (profile.isError) {
    return (
      <div className="inner" style={{ paddingTop: 32 }}>
        <ErrorNotice error={profile.error} onRetry={() => void profile.refetch()} />
      </div>
    );
  }

  const company: Company | undefined = profile.data?.company;
  const step: Step = company === undefined || editingCompany ? 'company' : 'reserve';

  return (
    <>
      <div style={{ borderBottom: '1px solid var(--hairline)', background: 'var(--canvas-soft)' }}>
        <div className="inner" style={{ paddingTop: 36, paddingBottom: 30 }}>
          <span className="eyebrow">Post a job</span>
          <h1 style={{ marginTop: 10, fontSize: 32 }}>
            Reserve your listing from <Money cents={MINIMUM_FIRST_BID_CENTS} />.
          </h1>
          <p className="muted" style={{ marginTop: 10, maxWidth: '62ch' }}>
            Pay to reserve your spot. Then paste the posting and we’ll fill in the details. Listings
            run {LISTING_LIFETIME_DAYS} days and renew free.
          </p>
          <div style={{ marginTop: 22 }}>
            <Steps current={step} />
          </div>
        </div>
      </div>

      <section className="section is-last" style={{ paddingTop: 32 }}>
        <div className="inner" style={{ maxWidth: 900 }}>
          {params.get('reserve') === 'cancelled' ? (
            <div style={{ marginBottom: 20 }}>
              <CautionNotice>
                Payment cancelled. Nothing was charged, and nothing was reserved.
              </CautionNotice>
            </div>
          ) : null}

          {step === 'company' ? (
            <>
              <p className="small muted" style={{ marginBottom: 16 }}>
                Candidates see this beside every listing you run.
              </p>
              <CompanyForm
                company={company}
                submitLabel={company === undefined ? 'Save and continue' : 'Save profile'}
                busy={saveCompany.isPending}
                serverError={saveCompany.isError ? errorMessage(saveCompany.error) : undefined}
                onSubmit={async (input) => {
                  await saveCompany.mutateAsync(input);
                  setEditingCompany(false);
                }}
              />
            </>
          ) : (
            <>
              <p className="small muted" style={{ marginBottom: 16 }}>
                Posting as <strong style={{ color: 'var(--ink)' }}>{company?.name}</strong>.{' '}
                <button type="button" className="linkbtn" onClick={() => setEditingCompany(true)}>
                  Edit company profile
                </button>
              </p>
              <ReserveStep />
            </>
          )}
        </div>
      </section>
    </>
  );
}

/** The global board, which is the only one that exists before a role does. */
const GLOBAL_SCOPE: LeaderboardScope = { kind: 'global' };

function ReserveStep(): ReactNode {
  const navigate = useNavigate();
  const reserve = useReserveListing();
  const { entries, isLoading, boardKnown, error, refetch } = useCompetingEntries(
    GLOBAL_SCOPE,
    '__unreserved__',
  );
  const totals = entries.map((entry) => entry.totalPaidCents);

  const [amount, setAmount] = useState(() => dollarInputValue(MINIMUM_FIRST_BID_CENTS));
  const parsed = parseDollarInput(amount);
  const preview = bidPreview({
    currentTotalCents: 0,
    competingTotalsCents: totals,
    desiredTotalCents: parsed.ok ? parsed.cents : undefined,
  });
  const problem = parsed.ok ? preview.message : parsed.message;

  async function submit(): Promise<void> {
    if (!parsed.ok || !preview.valid) return;
    try {
      const session = await reserve.mutateAsync(parsed.cents);
      // Stripe's return carries the listing id in its own URL, but a browser
      // that loses the query string still has this.
      rememberPublishing(session.jobId);
      window.location.assign(session.checkoutUrl);
    } catch {
      // Already on screen, held by the mutation.
    }
  }

  return (
    <div className="card is-padded stack" style={{ gap: 0 }}>
      <span className="eyebrow">Step 2 — reserve</span>
      <h2 style={{ marginTop: 8 }}>Choose what to spend</h2>
      <p className="muted" style={{ marginTop: 8, maxWidth: '58ch' }}>
        Rank is the total paid. Any whole-dollar amount from{' '}
        <Money cents={MINIMUM_FIRST_BID_CENTS} /> reserves a listing, and it lands wherever that
        total lands.
      </p>

      <div style={{ marginTop: 22 }}>
        <AmountStepper
          label="Amount to reserve"
          value={amount}
          onChange={setAmount}
          min={Math.round(MINIMUM_FIRST_BID_CENTS / 100)}
          hint="Whole dollars. Charged now, and it becomes this listing’s total."
          error={problem}
        />
      </div>

      <div className="receipt">
        <div className="line">
          <span>Where this sits on the main board today</span>
          <span data-testid="reserve-rank">
            {isLoading ? (
              'Checking the board…'
            ) : !boardKnown ? (
              <span className="muted">Couldn’t read the board.</span>
            ) : preview.valid ? (
              <>#{preview.projectedRank}</>
            ) : (
              <span className="muted">—</span>
            )}
          </span>
        </div>
        <div className="line">
          <span>Top of the main board costs</span>
          {boardKnown ? (
            <Money cents={preview.firstPlaceTotalCents} />
          ) : (
            <span className="muted">—</span>
          )}
        </div>
        <div className="total">
          <div>
            <div className="label">Charged today</div>
            <div className="note">One payment, non-refundable</div>
          </div>
          <span className="figure mono">
            <Odometer cents={preview.valid ? preview.chargeCents : 0} variant="field" />
          </span>
        </div>
      </div>

      <p className="preview-line" data-testid="reserve-preview" style={{ marginTop: 14 }}>
        Your specialism board is chosen with the posting on the next step, so the position above is
        the main board only.
      </p>

      {!boardKnown && !isLoading ? (
        <div style={{ marginTop: 14 }}>
          <ErrorNotice error={error} onRetry={refetch} />
        </div>
      ) : null}

      {reserve.isError ? (
        <div style={{ marginTop: 14 }}>
          <ErrorNotice error={reserve.error} />
        </div>
      ) : null}

      <button
        type="button"
        className="cta"
        style={{ marginTop: 18 }}
        disabled={!preview.valid || reserve.isPending}
        onClick={() => void submit()}
      >
        {reserve.isPending ? (
          'Opening payment…'
        ) : (
          <>
            Pay <Money cents={preview.valid ? preview.chargeCents : 0} /> and reserve
          </>
        )}
      </button>

      <p className="paynote">
        Payment is taken now. Your listing publishes as soon as you add the posting — the
        reservation does not expire, and it is not refundable. One payment counts on all three
        boards: global, category, and specialism.
      </p>

      <div className="modal-foot" style={{ marginTop: 4 }}>
        <ShieldIcon />
        <span>Payments are processed by Stripe. We never store card details.</span>
      </div>

      <p className="small muted" style={{ marginTop: 16 }}>
        Already paid for a listing that has no details yet?{' '}
        <button type="button" className="linkbtn" onClick={() => void navigate('/dashboard')}>
          Finish it from your dashboard
        </button>
        , or <Link to="/terms#payments">read the terms</Link>.
      </p>
    </div>
  );
}
